import { useState, useEffect } from 'react';
import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom';
import { AuthProvider, useAuth } from './contexts/AuthContext';
import Navbar from './components/Navbar';
import ProtectedRoute from './components/ProtectedRoute';
import LoadingScreen from './components/LoadingScreen';
import ReportMatchModal from './components/ReportMatchModal';
import Homepage from './pages/Homepage';
import ForClubs from './pages/ForClubs';
import SignUp from './pages/SignUp';
import Login from './pages/Login';
import Dashboard from './pages/Dashboard';
import Leaderboard from './pages/Leaderboard';
import PlayerProfile from './pages/PlayerProfile';
import Settings from './pages/Settings';
import MatchHistoryPage from './pages/MatchHistoryPage';
import Friends from './pages/Friends';
import Tournaments from './pages/Tournaments';
import TournamentCreate from './pages/TournamentCreate';
import TournamentView from './pages/TournamentView';
import ClubsHub from './pages/ClubsHub';
import ClubCreate from './pages/ClubCreate';
import ClubView from './pages/ClubView';
import Appearance from './pages/Appearance';
import Statistics from './pages/Statistics';
import Notifications from './pages/Notifications';
import ResetPassword from './pages/ResetPassword';
import PrivacyPolicy from './pages/PrivacyPolicy';
import TermsOfService from './pages/TermsOfService';
import './index.css';

/* ─────────────────────────────────────────────────────────────
   GUEST ROUTE — wraps pages that logged-in users shouldn't see
   (Log in, Sign up). If the user is already logged in, sends
   them straight to /dashboard instead.
   ───────────────────────────────────────────────────────────── */
function GuestRoute({ children }) {
  const { user, loading } = useAuth();
  if (loading) return <LoadingScreen />;
  if (user)    return <Navigate to="/dashboard" replace />;
  return children;
}

/* ─────────────────────────────────────────────────────────────
   APP ROUTES — lives inside AuthProvider so it can call useAuth
   via GuestRoute and ProtectedRoute.
   ───────────────────────────────────────────────────────────── */
function AppRoutes({ theme, setTheme, toggleTheme }) {
  const { user } = useAuth();

  // ── App-level Report Match modal ──────────────────────────
  // Triggered by navbar button OR by ranktt:openReportMatch event
  // (Dashboard dispatches the event with profile data so ELO preview works)
  const [reportOpen,        setReportOpen]        = useState(false);
  const [reportOpponent,    setReportOpponent]    = useState(null);
  const [reportEditMatch,   setReportEditMatch]   = useState(null);
  const [reportUserRating,  setReportUserRating]  = useState(null);
  const [reportUserMatches, setReportUserMatches] = useState(null);

  useEffect(() => {
    function onOpen(e) {
      const d = e.detail || {};
      setReportOpponent(d.opponent    ?? null);
      setReportEditMatch(d.editMatch  ?? null);
      setReportUserRating(d.userRating   ?? null);
      setReportUserMatches(d.userMatches ?? null);
      setReportOpen(true);
    }
    window.addEventListener('ranktt:openReportMatch', onOpen);
    return () => window.removeEventListener('ranktt:openReportMatch', onOpen);
  }, []);

  function closeReport() {
    setReportOpen(false);
    setReportOpponent(null);
    setReportEditMatch(null);
  }

  return (
    <div className="app">
      <Navbar theme={theme} toggleTheme={toggleTheme} />
      <Routes>
        {/* Marketing pages — public */}
        <Route path="/"          element={<Homepage />} />
        <Route path="/for-clubs" element={<ForClubs />} />

        {/* Auth pages — redirect to dashboard if already logged in */}
        <Route path="/signup" element={<GuestRoute><SignUp /></GuestRoute>} />
        <Route path="/login"  element={<GuestRoute><Login /></GuestRoute>} />

        {/* Password reset — public, no auth wrapper (Supabase injects a temp session via URL token) */}
        <Route path="/reset-password" element={<ResetPassword />} />

        {/* Legal pages — public */}
        <Route path="/privacy-policy" element={<PrivacyPolicy />} />
        <Route path="/terms-of-service" element={<TermsOfService />} />

        {/* App pages — redirect to login if not logged in */}
        <Route path="/dashboard"   element={<ProtectedRoute><Dashboard /></ProtectedRoute>} />
        <Route path="/leaderboard"   element={<ProtectedRoute><Leaderboard /></ProtectedRoute>} />
        <Route path="/profile/:username" element={<ProtectedRoute><PlayerProfile /></ProtectedRoute>} />
        <Route path="/settings" element={<ProtectedRoute><Settings theme={theme} setTheme={setTheme} toggleTheme={toggleTheme} /></ProtectedRoute>} />
        <Route path="/match-history/:username" element={<ProtectedRoute><MatchHistoryPage /></ProtectedRoute>} />
        <Route path="/friends" element={<ProtectedRoute><Friends /></ProtectedRoute>} />
        <Route path="/tournaments"      element={<ProtectedRoute><Tournaments /></ProtectedRoute>} />
        <Route path="/tournaments/new"  element={<ProtectedRoute><TournamentCreate /></ProtectedRoute>} />
        <Route path="/tournaments/:id"  element={<ProtectedRoute><TournamentView /></ProtectedRoute>} />
        <Route path="/clubs"      element={<ProtectedRoute><ClubsHub /></ProtectedRoute>} />
        <Route path="/clubs/new"  element={<ProtectedRoute><ClubCreate /></ProtectedRoute>} />
        <Route path="/clubs/:id"  element={<ProtectedRoute><ClubView /></ProtectedRoute>} />
        <Route path="/appearance"  element={<ProtectedRoute><Appearance theme={theme} setTheme={setTheme} /></ProtectedRoute>} />
        <Route path="/statistics"     element={<ProtectedRoute><Statistics /></ProtectedRoute>} />
        <Route path="/notifications"  element={<ProtectedRoute><Notifications /></ProtectedRoute>} />
      </Routes>

      {/* App-level Report Match modal — works from any page via ranktt:openReportMatch */}
      {user && (
        <ReportMatchModal
          isOpen={reportOpen}
          onClose={closeReport}
          currentUserId={user.id}
          currentUserRating={reportUserRating}
          currentUserMatches={reportUserMatches}
          editMatch={reportEditMatch}
          preSelectedOpponent={reportOpponent}
          onMatchSubmitted={() => {
            closeReport();
            // Refresh Dashboard + MatchHistory on any page that's listening
            window.dispatchEvent(new CustomEvent('ranktt:matchResolved'));
          }}
        />
      )}
    </div>
  );
}

function App() {
  // Read saved theme from localStorage when the app first loads.
  // If no saved preference exists, default to dark mode.
  const [theme, setTheme] = useState(() => {
    return localStorage.getItem('ranktt-theme') || 'dark';
  });

  // Whenever the theme changes, apply it to the <html> element
  // so our CSS variables kick in, and save it to localStorage.
  useEffect(() => {
    document.documentElement.setAttribute('data-theme', theme);
    localStorage.setItem('ranktt-theme', theme);
  }, [theme]);

  const DEFAULT_CYCLE = ['dark', 'midnight', 'flower', 'light'];

  const toggleTheme = () => {
    setTheme(prev => {
      // Navbar click always exits hacker mode
      if (prev === 'hacker') return 'dark';

      // If Advanced Mode is on, cycle through the user's custom selection
      const advancedOn = localStorage.getItem('ranktt-advanced-themes') === 'true';
      if (advancedOn) {
        const raw   = localStorage.getItem('ranktt-theme-cycle');
        let cycle = DEFAULT_CYCLE;
        try { cycle = raw ? JSON.parse(raw) : DEFAULT_CYCLE; } catch { /* corrupted, use default */ }
        if (cycle.length > 0) {
          const idx = cycle.indexOf(prev);
          return cycle[(idx + 1) % cycle.length];
        }
      }

      // Default 4-theme cycle
      if (prev === 'dark')     return 'midnight';
      if (prev === 'midnight') return 'flower';
      if (prev === 'flower')   return 'light';
      return 'dark';
    });
  };

  return (
    <AuthProvider>
      <BrowserRouter>
        <AppRoutes theme={theme} setTheme={setTheme} toggleTheme={toggleTheme} />
      </BrowserRouter>
    </AuthProvider>
  );
}

export default App;
